import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Image as ImageIcon, UploadCloud, Trash2, Activity, Square, Eye, Plus, RefreshCw, MessageSquare, Palette } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ColorEditorPopover } from './ColorEditorPopover';
import { UploadControl } from '@/components/UploadControl';
import { AutoplayVariation } from '@/store/videoFeatures/types/autoplay-ab-test';
import { templates } from './autoplayTemplates';
import { toast } from 'sonner';
import { X, Check } from 'lucide-react';

// Define quais templates usam quais campos e seus defaults
const TEMPLATE_FIELD_USAGE = {
  'small': { usesTextTop: true, usesTextBottom: false, defaultTop: 'Clique para ouvir', defaultBottom: '' },
  'autoPlay': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Ative o som', defaultBottom: 'Seu video já começou' },
  'large': { usesTextTop: false, usesTextBottom: true, defaultTop: '', defaultBottom: 'Clique para ouvir' },
  'fullScreen': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Seu vídeo já começou', defaultBottom: 'Ative o som' },
  'transparent': { usesTextTop: false, usesTextBottom: true, defaultTop: '', defaultBottom: 'Seu vídeo já começou' },
  'blur': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Conteúdo Sensível', defaultBottom: 'Este vídeo apresenta conteúdo que os grandes produtores não querem que você assista!' },
  'animation': { usesTextTop: true, usesTextBottom: false, defaultTop: 'Seu video já começou', defaultBottom: '' }
};

interface AutoplayVariationSheetProps {
  open: boolean;
  onClose: () => void;
  onSave: (variation: Omit<AutoplayVariation, 'id' | 'metrics'>) => void;
  existingVariation?: AutoplayVariation;
}

export const AutoplayVariationSheet: React.FC<AutoplayVariationSheetProps> = ({
  open,
  onClose,
  onSave,
  existingVariation
}) => {
  useLanguage();
  const [formData, setFormData] = useState({
    name: existingVariation?.name || '',
    templateId: existingVariation?.templateId || '',
    customization: {
      textTop: existingVariation?.customization?.textTop || 'Seu vídeo começou',
      textBottom: existingVariation?.customization?.textBottom || 'Toque para ativar o som',
      colors: {
        background: existingVariation?.customization?.colors?.background || 'rgba(255, 255, 255, 0.9)',
        text: existingVariation?.customization?.colors?.text || '#000000',
        icon: existingVariation?.customization?.colors?.icon || '#000000',
        border: existingVariation?.customization?.colors?.border || '#FFFFFF'
      },
      whiteBorder: existingVariation?.customization?.whiteBorder || false,
      pulseAnimation: existingVariation?.customization?.pulseAnimation || false,
      customImage: existingVariation?.customization?.customImage || undefined
    }
  });
  
  

  useEffect(() => {
    if (open) {
      if (existingVariation) {
        setFormData({
          name: existingVariation.name,
          templateId: existingVariation.templateId,
          customization: {
            ...existingVariation.customization,
            colors: {
              ...existingVariation.customization.colors,
              border: existingVariation.customization.colors?.border || '#FFFFFF'
            },
            whiteBorder: existingVariation.customization.whiteBorder ?? false,
            pulseAnimation: existingVariation.customization.pulseAnimation ?? false,
            customImage: existingVariation.customization.customImage ?? undefined
          }
        });
      } else {
        // Reset to default when creating new
        setFormData({
          name: '',
          templateId: '',
          customization: {
            textTop: 'Seu vídeo começou',
            textBottom: 'Toque para ativar o som',
            colors: {
              background: 'rgba(255, 255, 255, 0.9)',
              text: '#000000',
              icon: '#000000',
              border: '#FFFFFF'
            },
            whiteBorder: false,
            pulseAnimation: false,
            customImage: undefined
          }
        });
      }
    }
  }, [open, existingVariation]);

  const handleSave = (closeAfter = true) => {
    if (!formData.name.trim()) {
      toast.error(translateInterfaceText("Digite um nome para a variação"));
      return;
    }

    // Validação: template deve ser escolhido
    if (!formData.templateId || formData.templateId === '') {
      toast.error(translateInterfaceText("Escolha um template para a variação"));
      return;
    }

    // Validação específica para template custom
    if (formData.templateId === 'custom' && !formData.customization.customImage) {
      toast.error(translateInterfaceText("Envie uma imagem para o template personalizado"));
      return;
    }

    // Validação para templates com texto
    if (formData.templateId !== 'custom' || !formData.customization.customImage) {
      if (!formData.customization.textTop.trim() && !formData.customization.textBottom.trim()) {
        toast.error(translateInterfaceText("Preencha pelo menos um dos textos"));
        return;
      }
    }

    onSave(formData);
    toast.success(translateInterfaceText("Variação salva!"));
    
    if (closeAfter) {
      onClose();
    }
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    const fieldUsage = TEMPLATE_FIELD_USAGE[templateId as keyof typeof TEMPLATE_FIELD_USAGE];

    if (template) {
      // Template predefinido - aplica cores, defaults de texto, borda e pulsar
      setFormData(prev => ({
        ...prev,
        templateId,
        customization: {
          ...prev.customization,
          colors: { background: template.colors.background, text: template.colors.text, icon: template.colors.icon, border: template.colors.border || '' },
          textTop: fieldUsage?.defaultTop || '',
          textBottom: fieldUsage?.defaultBottom || '',
          whiteBorder: template.style?.hasWhiteBorder || false,
          pulseAnimation: template.style?.hasPulse || false,
          customImage: undefined
        }
      }));
    } else if (templateId === 'custom') {
      // Template personalizado
      setFormData(prev => ({
        ...prev,
        templateId: 'custom'
      }));
    }
  };

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setFormData(prev => ({
        ...prev,
        customization: {
          ...prev.customization,
          customImage: base64
        }
      }));
      toast.success(translateInterfaceText("Imagem carregada com sucesso!"));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      customization: {
        ...prev.customization,
        customImage: undefined
      }
    }));
    toast.info(translateInterfaceText("Imagem removida"));
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-[360px] p-0 flex flex-col overflow-hidden"
        hideCloseButton
      >
        <SheetHeader className="px-5 py-4 border-b bg-background sticky top-0 z-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg font-semibold text-foreground">
              {existingVariation ? translateInterfaceText("Editar Variação") : translateInterfaceText("Nova Variação")}
            </SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-primary hover:bg-primary/10 hover:text-primary transition-colors">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="flex-1 px-5 py-5 space-y-5 overflow-y-auto">
          {/* 1. Nome da Variação */}
          <div className="space-y-2">
            <Label htmlFor="variation-name" className="text-sm font-medium text-foreground">
              {translateInterfaceText("Nome da Variação")}</Label>
            <Input
              id="variation-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={translateInterfaceText("Ex: Autoplay Moderno, Conversão Limpa...")}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground">
              {translateInterfaceText("Nome usado apenas para organização interna.")}</p>
          </div>

          {/* Divisor */}
          <div className="h-px bg-border" />

          {/* 2. Template Base */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">{translateInterfaceText("Template Base")}</Label>
            <div className="grid grid-cols-2 gap-2.5">
              {templates.filter(t => t.id !== 'custom').map(template => {
                const isSelected = formData.templateId === template.id;
                return (
                  <div
                    key={template.id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    className={cn(
                      "relative overflow-hidden cursor-pointer rounded-xl bg-white",
                      "transition-all duration-200 ease-out",
                      "focus:outline-none focus:ring-1 focus:ring-[#1D4FFF]/30",
                      "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]",
                      isSelected
                        ? 'border-[2px] border-[#1D4FFF] shadow-sm'
                        : 'border border-[#E6E6F0] hover:border-[#1D4FFF]/40'
                    )}
                    onClick={() => handleTemplateChange(template.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTemplateChange(template.id);
                      }
                    }}
                  >
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#1D4FFF] rounded-full flex items-center justify-center shadow-sm z-10">
                        <Check size={12} className="text-white" strokeWidth={3} />
                      </div>
                    )}

                    {/* Preview interno com imagem SVG */}
                    <div className="p-2">
                      <div className="aspect-[4/3] w-full flex items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
                        <img
                          src={template.image}
                          alt={template.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      {/* Nome do template abaixo */}
                      <p className="text-[10px] font-medium text-center text-gray-700 dark:text-gray-300 mt-1.5">
                        {template.name}
                      </p>
                    </div>
                  </div>
                );
              })}

              {/* Card Personalizado */}
              <div
                role="button"
                tabIndex={0}
                aria-pressed={formData.templateId === 'custom'}
                className={cn(
                  "relative overflow-hidden cursor-pointer rounded-xl bg-white",
                  "border-2 border-dashed transition-all duration-200 ease-out",
                  "focus:outline-none focus:ring-1 focus:ring-[#1D4FFF]/30",
                  "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]",
                  formData.templateId === 'custom'
                    ? 'border-[#1D4FFF] bg-[#1D4FFF]/5 shadow-sm'
                    : 'border-[#CBD5E1] hover:border-[#1D4FFF]/40'
                )}
                onClick={() => handleTemplateChange('custom')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTemplateChange('custom');
                  }
                }}
              >
                {formData.templateId === 'custom' && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#1D4FFF] rounded-full flex items-center justify-center shadow-sm z-10">
                    <Check size={12} className="text-white" strokeWidth={3} />
                  </div>
                )}

                {/* Preview interno */}
                <div className="p-2">
                  <div className="aspect-[4/3] w-full flex flex-col items-center justify-center gap-1.5 rounded-lg bg-[#F8FAFC]">
                    {formData.customization.customImage ? (
                      <img
                        src={formData.customization.customImage}
                        alt={translateInterfaceText("Custom")}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D4FFF] to-[#60A5FA] flex items-center justify-center">
                          <Plus className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-[10px] font-medium text-center text-gray-700 dark:text-gray-300 mt-1.5">
                          {translateInterfaceText("Personalizado")}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upload de Imagem Personalizada */}
          {formData.templateId === 'custom' && (
            <>
              <div className="h-px bg-border" />
              <div className="space-y-3 autoplay-upload-section">
                <input
                  id="sheet-image-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleImageUpload(e.target.files[0]);
                    e.target.value = '';
                  }}
                />
              
              {!formData.customization.customImage ? (
                <div 
                  className="autoplay-upload-box cursor-pointer"
                  onClick={() => document.getElementById('sheet-image-upload')?.click()}
                >
                  <UploadCloud className="upload-icon" />
                  <p>{translateInterfaceText("Arraste seu arquivo ou clique para enviar")}</p>
                  <span>{translateInterfaceText("Tamanho recomendado: 1920x1080px • Formatos: JPG, PNG, GIF • Máximo: 10MB")}</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Preview com overlay de ações */}
                  <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-video group cursor-pointer">
                    {/* Background simulando vídeo */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900" />
                    
                    {/* Imagem personalizada completa */}
                    <div className="relative z-10 w-full h-full">
                      <img 
                        src={formData.customization.customImage} 
                        alt={translateInterfaceText("Overlay personalizado")}
                        className="w-full h-full object-cover rounded-xl shadow-lg transition-all duration-200 group-hover:brightness-75"
                        style={{
                          border: formData.customization.whiteBorder 
                            ? '2px solid rgba(255,255,255,0.12)' 
                            : 'none',
                          animation: formData.customization.pulseAnimation 
                            ? 'pulse 2s infinite' 
                            : 'none'
                        }}
                      />
                    </div>
                    
                    {/* Overlay com ações — aparece no hover */}
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-6 z-30 rounded-xl">
                      {/* Botão Trocar */}
                      <button 
                        type="button"
                        onClick={() => document.getElementById('sheet-image-upload')?.click()}
                        className="flex flex-col items-center gap-2 group/btn"
                      >
                        <div className="w-11 h-11 rounded-full bg-[#1D4FFF] flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                          <RefreshCw className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-white text-[12px] font-medium">{translateInterfaceText("Trocar")}</span>
                      </button>
                      
                      {/* Botão Remover */}
                      <button 
                        type="button"
                        onClick={handleRemoveImage}
                        className="flex flex-col items-center gap-2 group/btn"
                      >
                        <div className="w-11 h-11 rounded-full bg-red-500 flex items-center justify-center transition-transform duration-200 group-hover/btn:scale-110 shadow-lg">
                          <Trash2 className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-white text-[12px] font-medium">{translateInterfaceText("Remover")}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
            </div>
            </>
          )}

          {/* Accordion para Mensagem e Estilo */}
          <Accordion 
            type="multiple" 
            defaultValue={["mensagem"]} 
            className="w-full space-y-2"
          >
            {/* SEÇÃO 1 — Mensagem do Overlay */}
            {!(formData.templateId === 'custom' && formData.customization.customImage) && (
              <AccordionItem value="mensagem" className="border border-border rounded-xl overflow-hidden bg-card">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors [&[data-state=open]>div>svg]:text-primary">
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-sm font-semibold text-foreground">{translateInterfaceText("Mensagem")}</h3>
                      <p className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[200px]">
                        {formData.customization.textTop || formData.customization.textBottom 
                          ? `"${formData.customization.textTop || formData.customization.textBottom}"`
                          : translateInterfaceText("Texto do overlay")}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-3 pt-2">
                    {(() => {
                      const fieldUsage = TEMPLATE_FIELD_USAGE[formData.templateId as keyof typeof TEMPLATE_FIELD_USAGE] || TEMPLATE_FIELD_USAGE['autoPlay'];
                      const MAX_CHARS_TOP = 40;
                      const MAX_CHARS_BOTTOM = 90;

                      return (
                        <>
                          {/* Texto Principal */}
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Label
                                htmlFor="text-top"
                                className={cn(
                                  "text-xs font-medium",
                                  !fieldUsage.usesTextTop ? "text-muted-foreground/50" : "text-foreground"
                                )}
                              >
                                {translateInterfaceText("Mensagem Principal")}{!fieldUsage.usesTextTop && <span className="ml-1 text-[9px]">{translateInterfaceText("(não usado neste template)")}</span>}
                              </Label>
                              {fieldUsage.usesTextTop && (
                                <span className={cn(
                                  "text-[9px] font-medium tabular-nums",
                                  formData.customization.textTop.length >= MAX_CHARS_TOP ? "text-red-500" :
                                  formData.customization.textTop.length >= MAX_CHARS_TOP * 0.8 ? "text-amber-500" : "text-muted-foreground"
                                )}>
                                  {formData.customization.textTop.length}/{MAX_CHARS_TOP}
                                </span>
                              )}
                            </div>
                            <Input
                              id="text-top"
                              value={formData.customization.textTop}
                              onChange={(e) => {
                                if (e.target.value.length <= MAX_CHARS_TOP) {
                                  setFormData(prev => ({
                                    ...prev,
                                    customization: {
                                      ...prev.customization,
                                      textTop: e.target.value
                                    }
                                  }));
                                }
                              }}
                              placeholder={fieldUsage.usesTextTop ? fieldUsage.defaultTop || translateInterfaceText("Ex.: Seu vídeo já começou") : translateInterfaceText("Campo não usado neste template")}
                              disabled={!fieldUsage.usesTextTop}
                              className={cn(
                                "h-9 text-[12px]",
                                fieldUsage.usesTextTop
                                  ? "bg-muted/20 focus:border-primary focus:ring-primary/20 cursor-text"
                                  : "bg-muted/40 cursor-not-allowed text-muted-foreground/50 border-muted"
                              )}
                            />
                          </div>

                          {/* Texto de Apoio */}
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Label
                                htmlFor="text-bottom"
                                className={cn(
                                  "text-xs font-medium",
                                  !fieldUsage.usesTextBottom ? "text-muted-foreground/50" : "text-foreground"
                                )}
                              >
                                {translateInterfaceText("Mensagem Secundária")}{!fieldUsage.usesTextBottom && <span className="ml-1 text-[9px]">{translateInterfaceText("(não usado neste template)")}</span>}
                              </Label>
                              {fieldUsage.usesTextBottom && (
                                <span className={cn(
                                  "text-[9px] font-medium tabular-nums",
                                  formData.customization.textBottom.length >= MAX_CHARS_BOTTOM ? "text-red-500" :
                                  formData.customization.textBottom.length >= MAX_CHARS_BOTTOM * 0.8 ? "text-amber-500" : "text-muted-foreground"
                                )}>
                                  {formData.customization.textBottom.length}/{MAX_CHARS_BOTTOM}
                                </span>
                              )}
                            </div>
                            <Input
                              id="text-bottom"
                              value={formData.customization.textBottom}
                              onChange={(e) => {
                                if (e.target.value.length <= MAX_CHARS_BOTTOM) {
                                  setFormData(prev => ({
                                    ...prev,
                                    customization: {
                                      ...prev.customization,
                                      textBottom: e.target.value
                                    }
                                  }));
                                }
                              }}
                              placeholder={fieldUsage.usesTextBottom ? fieldUsage.defaultBottom || translateInterfaceText("Ex.: Toque para ativar o som") : translateInterfaceText("Campo não usado neste template")}
                              disabled={!fieldUsage.usesTextBottom}
                              className={cn(
                                "h-9 text-[12px]",
                                fieldUsage.usesTextBottom
                                  ? "bg-muted/20 focus:border-primary focus:ring-primary/20 cursor-text"
                                  : "bg-muted/40 cursor-not-allowed text-muted-foreground/50 border-muted"
                              )}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* SEÇÃO 2 — Estilo do Overlay */}
            <AccordionItem value="estilo" className="border border-border rounded-xl overflow-hidden bg-card">
              <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors [&[data-state=open]>div>svg]:text-primary">
                <div className="flex items-center gap-3 w-full">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Palette className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-sm font-semibold text-foreground">{translateInterfaceText("Estilo")}</h3>
                    {/* Resumo visual: cores + efeitos */}
                    <div className="flex items-center gap-2 mt-1">
                      {/* 4 cores */}
                      <div className="flex items-center gap-0.5">
                        <div 
                          className="w-3 h-3 rounded-full border border-border/50 shadow-sm"
                          style={{ backgroundColor: formData.customization.colors.background }}
                          title={translateInterfaceText("Fundo")}
                        />
                        <div 
                          className="w-3 h-3 rounded-full border border-border/50 shadow-sm"
                          style={{ backgroundColor: formData.customization.colors.text }}
                          title={translateInterfaceText("Texto")}
                        />
                        <div 
                          className="w-3 h-3 rounded-full border border-border/50 shadow-sm"
                          style={{ backgroundColor: formData.customization.colors.icon }}
                          title={translateInterfaceText("Ícone")}
                        />
                        <div 
                          className="w-3 h-3 rounded-full border border-border/50 shadow-sm"
                          style={{ backgroundColor: formData.customization.colors.border || '#FFFFFF' }}
                          title={translateInterfaceText("Borda")}
                        />
                      </div>
                      {/* Efeitos ativos */}
                      {(formData.customization.pulseAnimation || formData.customization.whiteBorder) && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          <span className="text-[10px] text-muted-foreground">
                            {[
                              formData.customization.pulseAnimation && 'Pulsar',
                              formData.customization.whiteBorder && 'Borda'
                            ].filter(Boolean).join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3 pt-2">
                  {/* Card 1: Cores */}
                  <div className="p-3 rounded-xl border border-border bg-background space-y-2.5">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      {translateInterfaceText("Cores")}</span>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {/* Card Fundo */}
                      <ColorEditorPopover
                        colorName={translateInterfaceText("Fundo")}
                        currentColor={formData.customization.colors.background}
                        onSave={(newColor) => setFormData(prev => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            colors: { ...prev.customization.colors, background: newColor }
                          }
                        }))}
                      >
                        <button 
                          type="button" 
                          className={cn(
                            "flex flex-col items-center gap-1.5 p-2.5 rounded-lg border transition-all",
                            "hover:border-primary hover:bg-primary/5",
                            "border-border bg-card"
                          )}
                        >
                          <div
                            className="w-7 h-7 rounded-full border-2 border-muted shadow-sm"
                            style={{ backgroundColor: formData.customization.colors.background }}
                          />
                          <span className="text-[10px] font-medium text-muted-foreground">{translateInterfaceText("Fundo")}</span>
                        </button>
                      </ColorEditorPopover>

                      {/* Card Texto */}
                      <ColorEditorPopover
                        colorName={translateInterfaceText("Texto")}
                        currentColor={formData.customization.colors.text}
                        onSave={(newColor) => setFormData(prev => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            colors: { ...prev.customization.colors, text: newColor }
                          }
                        }))}
                      >
                        <button 
                          type="button" 
                          className={cn(
                            "flex flex-col items-center gap-1.5 p-2.5 rounded-lg border transition-all",
                            "hover:border-primary hover:bg-primary/5",
                            "border-border bg-card"
                          )}
                        >
                          <div
                            className="w-7 h-7 rounded-full border-2 border-muted shadow-sm"
                            style={{ backgroundColor: formData.customization.colors.text }}
                          />
                          <span className="text-[10px] font-medium text-muted-foreground">{translateInterfaceText("Texto")}</span>
                        </button>
                      </ColorEditorPopover>

                      {/* Card Ícone de Som */}
                      <ColorEditorPopover
                        colorName={translateInterfaceText("Ícone de som")}
                        currentColor={formData.customization.colors.icon}
                        onSave={(newColor) => setFormData(prev => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            colors: { ...prev.customization.colors, icon: newColor }
                          }
                        }))}
                      >
                        <button 
                          type="button" 
                          className={cn(
                            "flex flex-col items-center gap-1.5 p-2.5 rounded-lg border transition-all",
                            "hover:border-primary hover:bg-primary/5",
                            "border-border bg-card"
                          )}
                        >
                          <div
                            className="w-7 h-7 rounded-full border-2 border-muted shadow-sm"
                            style={{ backgroundColor: formData.customization.colors.icon }}
                          />
                          <span className="text-[10px] font-medium text-muted-foreground">{translateInterfaceText("Ícone")}</span>
                        </button>
                      </ColorEditorPopover>

                      {/* Card Borda */}
                      <ColorEditorPopover
                        colorName={translateInterfaceText("Borda")}
                        currentColor={formData.customization.colors.border || '#FFFFFF'}
                        onSave={(newColor) => setFormData(prev => ({
                          ...prev,
                          customization: {
                            ...prev.customization,
                            colors: { ...prev.customization.colors, border: newColor }
                          }
                        }))}
                      >
                        <button 
                          type="button" 
                          className={cn(
                            "flex flex-col items-center gap-1.5 p-2.5 rounded-lg border transition-all",
                            "hover:border-primary hover:bg-primary/5",
                            "border-border bg-card"
                          )}
                        >
                          <div
                            className="w-7 h-7 rounded-full border-2 border-muted shadow-sm"
                            style={{ backgroundColor: formData.customization.colors.border || '#FFFFFF' }}
                          />
                          <span className="text-[10px] font-medium text-muted-foreground">{translateInterfaceText("Borda")}</span>
                        </button>
                      </ColorEditorPopover>
                    </div>
                  </div>

                  {/* Card 2: Efeitos */}
                  <div className="p-3 rounded-xl border border-border bg-background space-y-2">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      {translateInterfaceText("Efeitos")}</span>
                    
                    {/* Pulsar */}
                    <div className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-2">
                        <Activity className={cn("h-3.5 w-3.5", formData.customization.pulseAnimation ? "text-primary" : "text-muted-foreground")} />
                        <span className="text-xs font-medium">{translateInterfaceText("Pulsar")}</span>
                      </div>
                      <Switch
                        checked={formData.customization.pulseAnimation}
                        onCheckedChange={(checked) => setFormData(prev => ({
                          ...prev,
                          customization: { ...prev.customization, pulseAnimation: checked }
                        }))}
                      />
                    </div>
                    
                    {/* Borda Branca */}
                    <div className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-2">
                        <Square className={cn("h-3.5 w-3.5", formData.customization.whiteBorder ? "text-primary" : "text-muted-foreground")} />
                        <span className="text-xs font-medium">{translateInterfaceText("Borda branca")}</span>
                      </div>
                      <Switch
                        checked={formData.customization.whiteBorder}
                        onCheckedChange={(checked) => setFormData(prev => ({
                          ...prev,
                          customization: { ...prev.customization, whiteBorder: checked }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <SheetFooter className="px-5 py-4 border-t bg-background sticky bottom-0 z-50 shadow-[0_-1px_3px_rgba(0,0,0,0.05)]">
          <Button 
            onClick={() => handleSave(true)} 
            className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-all"
          >
            {translateInterfaceText("Salvar alterações")}</Button>
        </SheetFooter>
      </SheetContent>

    </Sheet>
  );
};
